export const ARCHETYPES = [
  "Builder","Thinker","Creator","Connector","Organizer","Leader","Caregiver","Advocate",
] as const;
export type Archetype = (typeof ARCHETYPES)[number];
export type Confidence = "high" | "medium" | "low";
export type MarketSource = "live" | "seed" | "fallback" | "unpriced";

export interface Career {
  ciId: string; title: string; family: string; cluster: string; level: string;
  entryRequirements: string; confidence: Confidence;
  fields: Record<string, string | null>;
  dna: Partial<Record<Archetype, number>>;
  salaryLo: number | null; salaryHi: number | null;
  demand2040: string | null; automationExposure: number | null;
  market: MarketSource;
  /** Non-seed row older than 24h. Used and flagged, never discarded. */
  marketStale?: boolean;
  /** How many postings the band came from, when the row is scraped. */
  observations?: number | null;
}

export type StudyCapacity = "none" | "short" | "parttime" | "degree";
export type ImpactDomain = "care" | "grow" | "planet" | "learn" | "systems";

export interface Learner {
  prefOutdoor: number; prefMovement: number; prefScreenTime: number;
  prefTravel: number; prefStableSchedule: number;
  tolPublicSpeaking: number; tolPeopleContact: number;
  tolClosePhysical: number; tolSensory: number;
  prefAutonomy: number; prefImpactVisibility: number; prefIncomeUpside: number;
  prefPrestige: number; prefStability: number; prefCreativity: number;
  prefFocusVsSwitching: number; prefSoloVsTeam: number;
  tolSocialLoad: number; tolEmotionalLoad: number; tolGruntWork: number;
  archetypePrimary: Archetype; archetypeSecondary: Archetype;
  impactDomain1?: ImpactDomain; impactDomain2?: ImpactDomain;
  salaryFloorLpa: number; studyCapacity: StudyCapacity;
  holdsDegree: boolean; yearsExperience: number;
  /**
   * Career families the learner named as passions. AFFINITY ONLY: worth a
   * small additive bonus (`PASSION_BONUS`) after the weighted score, and
   * never read by the eligibility gate. A stated interest must not be able
   * to delete a career, nor to outweigh revealed preference.
   */
  passionFamilies?: ReadonlySet<string>;
  /**
   * How many of the learner's picks point at each family. Lets a family
   * they named twice count for more than one they named once, without ever
   * exceeding `PASSION_BONUS_STRONG`. Optional — falls back to
   * `passionFamilies` when absent.
   */
  passionFamilyStrength?: ReadonlyMap<string, number>;
  /**
   * X-MOBILITY-001. How far the learner could move to study/work:
   * 0 = home only, 1 = own state (also "not sure"), 2 = country, 3 = world.
   * BOUNDED ADJUSTMENT ONLY (±`MOBILITY_ADJUST`), applied after the weighted
   * total and never read by the eligibility gate — a statement of what is
   * possible must not delete careers. Absent = no adjustment at all, so
   * every fixture predating this field stays bit-identical.
   */
  studyMobilityReach?: number;
}


const MAPS: Record<string, Record<string, number>> = {
  Context_IndoorOutdoor: { Mostly_Indoor: 0, Mixed: 50, Mostly_Outdoor: 100 },
  Context_MovementLevel: { Mostly_Sitting: 0, Mixed: 50, On_the_Move: 100 },
  Context_PublicSpeakingDemand: { Low: 0, Medium: 50, High: 100 },
  Context_PeopleContactLevel: { Low: 0, Medium: 40, High: 75, High_Distress: 100 },
  Context_TravelIntensity: { None: 0, Low: 25, Occasional: 50, Medium: 50, Frequent: 100 },
  Context_ScreenTime: { Low: 0, Medium: 50, High: 100 },
  Context_SchedulePattern: { Intense_Irregular: 0, Variable: 50, Predictable: 100 },
  Context_ClosePhysicalWork: { No: 0, Yes: 100 },
  Context_SensoryExposure: { No_Special_Exposure: 0, Smell_Noise: 40, Hazardous_Materials: 70, Body_Fluids: 100 },
  Value_Autonomy: { Low: 0, Medium: 50, High: 100 },
  Value_ImpactVisibility: { Low: 0, Medium: 50, High: 100 },
  Value_IncomeUpside: { Low: 0, Medium: 50, High: 100 },
  Value_Prestige: { Low: 0, Medium: 50, High: 100 },
  Value_Stability: { Low: 0, Medium: 50, High: 100 },
  Value_Creativity: { Low: 0, Medium: 50, High: 100 },
  Energy_SocialLoad: { Low: 0, Medium: 50, High: 100 },
  Energy_RecoveryDifficulty: { Low: 0, Medium: 50, High: 100 },
  Energy_CognitiveMode: { Deep_Focus: 0, Mixed: 50, Context_Switching: 100 },
  Energy_CollaborationStyle: { Solo: 0, Mixed: 50, Collaborative: 100 },
  GruntWork_Intensity: { Low: 0, Medium: 50, High: 100 },
};
const DEMAND_2040: Record<string, number> = {
  "Very High": 100, High: 80, "High to Very High": 90,
  "Medium-High": 65, Medium: 50, Stable: 55, Low: 20,
};

function num(c: Career, field: string, fallback = 50): number {
  const v = c.fields[field];
  if (v == null) return fallback;
  const m = MAPS[field];
  return m && v in m ? m[v] : fallback;
}

/** Symmetric. Both directions are a mismatch. Use for preferences. */
export const prefFit = (learner: number, career: number) => 100 - Math.abs(learner - career);

/**
 * Asymmetric. Use for tolerances. Demand above tolerance hurts hard; tolerance
 * above demand barely matters — being tougher than the job needs is not a misfit.
 * INVERTING THIS IS THE MOST LIKELY BUG IN THE ENGINE and it still produces
 * plausible-looking output. The clinician golden test is the canary.
 */
export function tolFit(tolerance: number, demand: number, overPenalty = 0.15): number {
  if (demand <= tolerance) return 100 - (tolerance - demand) * overPenalty;
  return Math.max(0, 100 - (demand - tolerance) * 1.35);
}

const weighted = (parts: Array<[number, number]>) => {
  const tw = parts.reduce((s, [, w]) => s + w, 0);
  return parts.reduce((s, [v, w]) => s + v * w, 0) / tw;
};

export function scoreContext(L: Learner, c: Career) {
  const d = {
    outdoor:  prefFit(L.prefOutdoor,        num(c, "Context_IndoorOutdoor")),
    movement: prefFit(L.prefMovement,       num(c, "Context_MovementLevel")),
    screen:   prefFit(L.prefScreenTime,     num(c, "Context_ScreenTime")),
    travel:   prefFit(L.prefTravel,         num(c, "Context_TravelIntensity")),
    schedule: prefFit(L.prefStableSchedule, num(c, "Context_SchedulePattern")),
    speaking: tolFit(L.tolPublicSpeaking,   num(c, "Context_PublicSpeakingDemand")),
    people:   tolFit(L.tolPeopleContact,    num(c, "Context_PeopleContactLevel")),
    physical: tolFit(L.tolClosePhysical,    num(c, "Context_ClosePhysicalWork")),
    sensory:  tolFit(L.tolSensory,          num(c, "Context_SensoryExposure")),
  };
  const score = weighted([
    [d.outdoor, 1], [d.movement, 1], [d.screen, 1], [d.travel, 0.8], [d.schedule, 1],
    [d.speaking, 1], [d.people, 1.2], [d.physical, 1.2], [d.sensory, 1.2],
  ]);
  return { score, detail: d };
}

export function scoreValues(L: Learner, c: Career) {
  const d = {
    autonomy:   prefFit(L.prefAutonomy,         num(c, "Value_Autonomy")),
    impact:     prefFit(L.prefImpactVisibility, num(c, "Value_ImpactVisibility")),
    prestige:   prefFit(L.prefPrestige,         num(c, "Value_Prestige")),
    stability:  prefFit(L.prefStability,        num(c, "Value_Stability")),
    creativity: prefFit(L.prefCreativity,       num(c, "Value_Creativity")),
    focus:      prefFit(L.prefFocusVsSwitching, num(c, "Energy_CognitiveMode")),
    soloTeam:   prefFit(L.prefSoloVsTeam,       num(c, "Energy_CollaborationStyle")),
    socialLoad: tolFit(L.tolSocialLoad,         num(c, "Energy_SocialLoad")),
    emotional:  tolFit(L.tolEmotionalLoad,      num(c, "Energy_RecoveryDifficulty")),
    grind:      tolFit(L.tolGruntWork,          num(c, "GruntWork_Intensity")),
  };
  const score = weighted([
    [d.autonomy, 1], [d.impact, 1], [d.prestige, 0.7], [d.stability, 1], [d.creativity, 1],
    [d.focus, 1], [d.soloTeam, 1], [d.socialLoad, 1.2], [d.emotional, 1.2], [d.grind, 1],
  ]);
  return { score, detail: d };
}

export function scoreArchetype(L: Learner, c: Career): number {
  // A career with no archetype profile is unknown, not a mismatch — score it
  // neutral rather than throwing the whole match run away.
  const dna = c.dna ?? null;
  const keys = (dna ? Object.keys(dna) : []) as Archetype[];
  if (keys.length === 0) return 45;

  const p = dna[L.archetypePrimary] ?? 0;
  const s = dna[L.archetypeSecondary] ?? 0;
  if (p === 0 && s === 0) return Math.max(...keys.map((k) => dna[k]!)) * 100 * 0.2;
  return Math.min(100, ((p * 0.7 + s * 0.3) * 100) / 0.85);
}

/**
 * The corpus carries TWO family taxonomies side by side: legacy names on the
 * 2,756 bridged careers ("Healthcare & Medicine") and registry names on the
 * newer 3,121 ("Health & Social Care"). Every family from BOTH must appear
 * here — a family that's missing scores a flat 25 on a 20%-weight slot, so
 * those careers can never rank however well they actually fit. Adding a new
 * family? Add its legacy alias too.
 */
const DOMAIN_FAMILIES: Record<ImpactDomain, ReadonlySet<string>> = {
  care: new Set(["Healthcare & Medicine","Social Work & NGO","Sports & Fitness","Education & Training",
    "Pharma & Biotech","Pharmaceuticals Biotech","Pharma & Biotech (Frontier)","Pharmaceuticals & Biotech",
    // registry taxonomy
    "Health & Social Care","Personal & Community Services","Sports & Recreation"]),
  grow: new Set(["Sales & Business Development","Marketing & Growth","Entrepreneurship & Startups",
    "Consulting & Strategy","Finance & Accounting","FinTech & Digital Finance","Product Management",
    "Retail & Consumer Services","Real Estate & Construction",
    // registry taxonomy
    "Business & Administration","Marketing & Communications"]),
  planet: new Set(["Green Tech & Climate Action","Energy & Environment","Agriculture & Food Production",
    "Electric Mobility & Battery Tech","Regional & Rural Specific",
    // registry taxonomy
    "Agriculture & Environment"]),
  learn: new Set(["Education & Training","Creative Arts & Media","Journalism & Media",
    "Design & User Experience","India-Specific Roles",
    // registry taxonomy
    "Arts, Culture & Heritage","Creator Economy & Digital"]),
  systems: new Set(["Technology & Software","Data & Analytics","Engineering (Non-IT)",
    "Manufacturing & Industrial","Transportation & Logistics","Government & Public Sector",
    "Legal & Compliance","Human Resources","Architecture & Urban Planning","AI & Emerging Technologies",
    "Semiconductors & Electronics","Space & Aerospace","Military & Defense","Blue-Collar & Trade",
    "Research & Development","Hospitality & Tourism","Informal Sector & Gig Economy",
    // registry taxonomy
    "Skilled Trades & Construction","Public Service & Safety","Frontier & Emerging",
    "Manufacturing & Production","Science & Research","Engineering","Transport & Logistics"]),
};

export function scoreDomain(L: Learner, c: Career): number {
  if (L.impactDomain1 && DOMAIN_FAMILIES[L.impactDomain1].has(c.family)) return 100;
  if (L.impactDomain2 && DOMAIN_FAMILIES[L.impactDomain2].has(c.family)) return 80;
  return 25;
}

export function scoreSalary(L: Learner, c: Career): number {
  const lo = c.salaryLo, hi = c.salaryHi;
  if (hi == null || lo == null || hi <= 0) return 50;
  const floor = L.salaryFloorLpa;
  // Proportional, not flat: missing an ₹8 floor by ₹4 is half the income;
  // missing ₹20 by ₹4 is a rounding error.
  if (hi < floor) return Math.max(0, 100 - ((floor - hi) / Math.max(floor, 1)) * 160);
  const mid = (lo + hi) / 2;
  const upside = Math.min(100, (mid / Math.max(floor, 1) - 1) * 55);
  const care = L.prefIncomeUpside / 100;
  return 60 + upside * 0.4 * care + (1 - care) * 20;
}

export function scoreDemand(c: Career): number {
  const label = (c.demand2040 ?? "").trim();
  const base = DEMAND_2040[label] ?? DEMAND_2040[label.split("(")[0].trim()] ?? 60;
  const auto = c.automationExposure ?? 50;
  return 0.6 * base + 0.4 * (100 - auto);
}

const STUDY_RANK: Record<StudyCapacity, number> = { none: 0, short: 1, parttime: 2, degree: 3 };
const RE_POSTGRAD = /\b(phd|doctorate|mbbs|md\b|llb|llm|mba|master|m\.?tech|m\.?sc|post ?grad|residency|bar exam)/;
const RE_DEGREE   = /\b(btech|b\.?e\b|bachelor|b\.?des|b\.?sc|bca|bcom|\bba\b|degree|graduation)/;
const RE_VOC      = /\b(diploma|iti\b|polytechnic|certification|certificate|bootcamp|apprenticeship)/;
const RE_OPEN     = /\b(no formal|portfolio|self-?taught|on-?the-?job|10\+2|12th|experience)/;

function branchCost(t: string): number {
  if (RE_POSTGRAD.test(t)) return 3;
  if (RE_DEGREE.test(t)) return 3;
  if (RE_VOC.test(t)) return 2;
  if (RE_OPEN.test(t)) return 1;
  return 2;
}

/** Cheapest viable route. "BTech OR portfolio OR bootcamp" -> the bootcamp. */
export function entryCost(c: Career): number {
  const t = (c.entryRequirements ?? "").toLowerCase();
  if (!t.trim()) return 2;
  const branches = t.split(/\bor\b|\/(?=[a-z])/);
  return branches.length ? Math.min(...branches.map(branchCost)) : branchCost(t);
}

/** Floored at 1. An empty result set is a product failure, not a filter. */
export function effectiveCapacity(L: Learner): number {
  let cap = STUDY_RANK[L.studyCapacity];
  if (L.holdsDegree) cap += 2;
  if (L.yearsExperience >= 10) cap += 2;
  else if (L.yearsExperience >= 3) cap += 1;
  return Math.max(1, cap);
}

const DISTANCE = ["Lateral move", "Short reskill", "Retrain 1-2 yrs", "Full requalification"] as const;
export type Distance = (typeof DISTANCE)[number];
function transitionDistance(L: Learner, need: number): Distance {
  const base = L.holdsDegree && need <= 3 ? Math.max(0, need - 2) : need;
  return DISTANCE[Math.min(3, base)];
}

/**
 * Additive bonus for a career whose family the learner named as a passion.
 * Deliberately small: it lifts work they said they cared about into view
 * without letting three taps promote a genuine misfit. Applied AFTER the
 * weighted total, so the six slot weights still sum to 1 and every golden
 * fixture without passions is bit-identical to before this existed.
 */
export const PASSION_BONUS = 7;
/** A family the learner named more than once. Still under the smallest slot. */
export const PASSION_BONUS_STRONG = 10;
/**
 * How many of the top-10 are reserved for work the learner said they're
 * drawn to, when eligible passion-family careers exist. This is a
 * VISIBILITY guarantee, not a score change: promoted careers keep the tier
 * their own score earned, so a passion can put a career in front of you but
 * can never call it a Best match.
 */
export const MIN_PASSION_SLOTS = 3;

export function scorePassion(L: Learner, c: Career): number {
  const strength =
    L.passionFamilyStrength?.get(c.family) ??
    (L.passionFamilies?.has(c.family) ? 1 : 0);
  if (strength >= 2) return PASSION_BONUS_STRONG;
  return strength >= 1 ? PASSION_BONUS : 0;
}

/**
 * X-MOBILITY-001 — bounded travel/mobility adjustment.
 *
 * Some work only makes sense if you can move for it: frequent travel,
 * postings wherever the employer needs you. A learner who told us they
 * could study anywhere in the world should see more of that work; one who
 * can only stay where they live now should see less of it — but never zero,
 * because circumstances change and this was never a filter.
 *
 * Returns a value in [-MOBILITY_ADJUST, +MOBILITY_ADJUST], smaller than the
 * smallest slot weight, and 0 whenever reach is unknown.
 */
export const MOBILITY_ADJUST = 4;

export function scoreMobility(L: Learner, c: Career): number {
  const reach = L.studyMobilityReach;
  if (reach == null) return 0;
  const travel = num(c, "Context_TravelIntensity"); // 0..100
  if (travel <= 25) {
    // Location-stable work: a small lift for the learner who must stay put.
    return reach === 0 ? MOBILITY_ADJUST / 2 : 0;
  }
  if (travel < 75) return 0; // ordinary travel load — no opinion either way
  // Travel-heavy work.
  if (reach >= 3) return MOBILITY_ADJUST;
  if (reach === 2) return MOBILITY_ADJUST / 2;
  if (reach === 1) return 0;
  return -MOBILITY_ADJUST; // home only — shown, just not led with
}


export const WEIGHTS = {
  archetype: 0.22, domain: 0.20, context: 0.18, values: 0.18, salary: 0.11, demand: 0.11,
} as const;

export type Tier = "Best match" | "Also consider" | "Explore" | "Below";

export interface MatchResult {
  ciId: string; title: string; family: string;
  score: number; tier: Tier; distance: Distance;
  archetype: number; domain: number; context: number; values: number;
  salary: number; demand: number;
  /** Passion family bonus applied, 0 when none. Shown as a "why". */
  passion: number;
  /**
   * X-MOBILITY-001 adjustment applied, signed, 0 when reach is unknown or
   * the career has no opinion about it. Shown as a "why".
   */
  mobility: number;
  salaryLo: number | null; salaryHi: number | null;
  confidence: Confidence; market: MarketSource;
  marketStale?: boolean; observations?: number | null;
  contextDetail: Record<string, number>; valuesDetail: Record<string, number>;
}

export function matchCareers(L: Learner, careers: Career[], topN = 10): {
  results: MatchResult[]; eligiblePool: number;
} {
  const baseCap = effectiveCapacity(L);
  const needs = new Map<string, number>();
  for (const c of careers) needs.set(c.ciId, entryCost(c));
  // Eligibility must never return empty. Someone who can't study right now
  // still gets the cheapest routes the corpus has — 35% of careers carry a
  // derived requirement, and a blank one costs 2 by default, which would
  // otherwise lock out every learner at capacity 1. Widening the cap only
  // ever happens when the honest cap yields nothing.
  const cheapest = needs.size ? Math.min(...needs.values()) : baseCap;
  const cap = Math.max(baseCap, cheapest);
  const scored: MatchResult[] = [];
  for (const c of careers) {
    const need = needs.get(c.ciId) ?? entryCost(c);
    if (need > cap) continue;

    const ctx = scoreContext(L, c);
    const val = scoreValues(L, c);
    const arc = scoreArchetype(L, c);
    const dom = scoreDomain(L, c);
    const sal = scoreSalary(L, c);
    const dem = scoreDemand(c);
    const pas = scorePassion(L, c);
    const mob = scoreMobility(L, c);
    const total = Math.max(0, Math.min(
      100,
      arc * WEIGHTS.archetype + dom * WEIGHTS.domain + ctx.score * WEIGHTS.context +
        val.score * WEIGHTS.values + sal * WEIGHTS.salary + dem * WEIGHTS.demand +
        pas + mob,
    ));
    // Penalty, not elimination: below-floor careers are still shown, never
    // presented as the best answer.
    const belowFloor = c.salaryHi != null && c.salaryHi < L.salaryFloorLpa;
    const capped = c.market === "unpriced" || c.confidence === "low" || belowFloor;
    const tier: Tier =
      total >= 80 && capped ? "Also consider"
      : total >= 80 ? "Best match"
      : total >= 65 ? "Also consider"
      : total >= 50 ? "Explore" : "Below";
    scored.push({
      ciId: c.ciId, title: c.title, family: c.family,
      score: Math.round(total * 10) / 10, tier, distance: transitionDistance(L, need),
      archetype: Math.round(arc * 10) / 10, domain: Math.round(dom * 10) / 10,
      context: Math.round(ctx.score * 10) / 10, values: Math.round(val.score * 10) / 10,
      salary: Math.round(sal * 10) / 10, demand: Math.round(dem * 10) / 10,
      passion: pas,
      mobility: mob,
      salaryLo: c.salaryLo, salaryHi: c.salaryHi,
      confidence: c.confidence, market: c.market,
      marketStale: c.marketStale ?? false, observations: c.observations ?? null,
      contextDetail: Object.fromEntries(Object.entries(ctx.detail).map(([k, v]) => [k, Math.round(v)])),
      valuesDetail: Object.fromEntries(Object.entries(val.detail).map(([k, v]) => [k, Math.round(v)])),
    });
  }
  // Codepoint tie-break, not localeCompare: ICU collation varies by host, so
  // equal-scoring careers ordered differently locally and in CI. A release
  // gate can't depend on the machine's locale.
  scored.sort((a, b) =>
    b.score - a.score || (a.title < b.title ? -1 : a.title > b.title ? 1 : 0));

  const seen = new Map<string, number>();
  const out: MatchResult[] = [];
  for (const r of scored) {
    const n = seen.get(r.family) ?? 0;
    if (n >= 2) continue;
    seen.set(r.family, n + 1);
    out.push(r);
    if (out.length >= topN) break;
  }

  // Passion visibility. If the learner named things they're drawn to and
  // none (or barely any) of that work surfaced, swap the weakest non-passion
  // rows for the strongest passion rows. Tiers are untouched.
  const wantsPassion =
    (L.passionFamilyStrength?.size ?? L.passionFamilies?.size ?? 0) > 0;
  if (wantsPassion && out.length >= MIN_PASSION_SLOTS) {
    const isPassion = (r: MatchResult) => r.passion > 0;
    const target = Math.min(MIN_PASSION_SLOTS, topN);
    const candidates = scored.filter(
      (r) => isPassion(r) && !out.some((o) => o.ciId === r.ciId),
    );
    while (out.filter(isPassion).length < target && candidates.length > 0) {
      const next = candidates.shift()!;
      if ((seen.get(next.family) ?? 0) >= 2) continue;
      // Drop the weakest non-passion row, never a passion row.
      let dropIdx = -1;
      for (let i = out.length - 1; i >= 0; i--) {
        if (!isPassion(out[i])) { dropIdx = i; break; }
      }
      if (dropIdx === -1) break;
      const dropped = out[dropIdx];
      seen.set(dropped.family, Math.max(0, (seen.get(dropped.family) ?? 1) - 1));
      out.splice(dropIdx, 1);
      seen.set(next.family, (seen.get(next.family) ?? 0) + 1);
      out.push(next);
    }
    out.sort((a, b) =>
      b.score - a.score || (a.title < b.title ? -1 : a.title > b.title ? 1 : 0));
  }

  return { results: out, eligiblePool: scored.length };
}
